// DataLens から nginx の受け渡しページ経由で sessionStorage に保存された
// 所有者別の物件コードリストを読み出す汎用関数

export const DATALENS_BULK_PROPERTY_GROUPS_KEY = "datalensBulkPropertyGroups";

// 受け渡しデータの有効期限（24時間 = 24 * 60 * 60 * 1000）。nginx 側が埋め込む savedAt を起点に判定する
export const BULK_PAYLOAD_TTL_MS = 86400000;

/**
 * 会社名と物件コードの組を会社名ごとのグループへ畳む。
 * 会社名・コードとも初出順を保ち、同じ会社名で重複するコードは1つにまとめる。
 * 照合の分割は同一コードが同じチャンクに入ることを前提にしているため、
 * ここで順序が入れ替わると分割ありと分割なしで照合結果が食い違う。
 */
export const groupCodesByName = (
  entries: Iterable<readonly [string, string]>
): DatalensBulkPropertyGroup[] => {
  const codesByName = new Map<string, Set<string>>();
  for (const [name, code] of entries) {
    const codes = codesByName.get(name) ?? new Set<string>();
    codes.add(code);
    codesByName.set(name, codes);
  }
  return [...codesByName].map(([name, codes]) => ({
    name,
    codes: [...codes],
  }));
};

export interface DatalensBulkPropertyGroup {
  name: string;
  codes: string[];
}

// nginx 側と同じ許可リスト。改ざんや別経路での書き込みに備えて読み出し時にも再検証する
const UUID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// nginx 側と同じ所有者名の上限長（連携仕様では 256 文字以内）
const MAX_NAME_LENGTH = 256;

const isValidGroup = (value: unknown): value is DatalensBulkPropertyGroup => {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const { name, codes } = value as { name?: unknown; codes?: unknown };
  return (
    typeof name === "string" &&
    name.trim().length > 0 &&
    name.length <= MAX_NAME_LENGTH &&
    Array.isArray(codes) &&
    codes.length > 0 &&
    codes.every(
      (code): code is string =>
        typeof code === "string" && UUID_PATTERN.test(code)
    )
  );
};

/**
 * sessionStorage から所有者別の物件コードリストを読み出し、同時に削除する（消費）。
 * - 読み出しは1回限り: リロードや再訪で同じデータから依頼をやり直せると二重依頼につながるため
 * - null を返す場合: savedAt が TTL(24時間) 超過 / 値が存在しない / JSON として不正 /
 *   形式不正（UUID 以外の混入等） / 旧形状（savedAt なしの配列直置き）
 * - 同名の所有者はひとつにまとめ、所有者内の重複コードは取り除く:
 *   重複した物件コードを照合APIへ送ると無駄な重複送信になり、一覧にも重複行が表示されるため
 */
export const readDatalensBulkPropertyGroups = ():
  | DatalensBulkPropertyGroup[]
  | null => {
  const stored = sessionStorage.getItem(DATALENS_BULK_PROPERTY_GROUPS_KEY);
  sessionStorage.removeItem(DATALENS_BULK_PROPERTY_GROUPS_KEY);
  if (stored === null) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return null;
  }

  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null;
  }
  const { savedAt, groups } = parsed as { savedAt?: unknown; groups?: unknown };
  if (typeof savedAt !== "number" || Number.isNaN(savedAt)) {
    return null;
  }
  if (savedAt + BULK_PAYLOAD_TTL_MS < Date.now()) {
    return null;
  }

  if (!Array.isArray(groups) || groups.length === 0) {
    return null;
  }

  const entries: Array<[string, string]> = [];
  for (const item of groups) {
    if (!isValidGroup(item)) {
      return null;
    }
    const name = item.name.trim();
    item.codes.forEach((code) => entries.push([name, code]));
  }

  return groupCodesByName(entries);
};
